// Set webpack public path for dynamic chunk loading.
// eslint-disable-next-line camelcase
if (window.mwl_settings?.plugin_url) {
  // eslint-disable-next-line camelcase
  __webpack_public_path__ = window.mwl_settings.plugin_url;
}

// Add version query string to chunk URLs for cache busting.
// This ensures CDN-cached chunks are invalidated when the plugin updates.
if (window.mwl_settings?.version) {
  const originalGetChunkScriptFilename = __webpack_require__.u;
  __webpack_require__.u = (chunkId) => {
    return originalGetChunkScriptFilename(chunkId) + '?ver=' + window.mwl_settings.version;
  };
}

import { render } from 'preact';
import { apiUrl, restNonce, anti_selector, selector, backdrop_opacity, theme, skip_dynamic_fetch } from './settings';
import { mwl_log, createOrphanMwlDataEntry } from './photoswipe/utils';
import PhotoSwipeCore from './photoswipe/core';

// Loading overlay helpers
const showLoadingOverlay = () => {
  const existingOverlay = document.getElementById('mwl-loading-overlay');
  if (existingOverlay) return;

  // Determine background color based on theme
  const isDarkTheme = !theme.includes('light');
  const bgColor = isDarkTheme ? `rgba(0, 0, 0, ${backdrop_opacity / 100})` : `rgba(255, 255, 255, ${backdrop_opacity / 100})`;
  const textColor = isDarkTheme ? '#fff' : '#333';

  const overlay = document.createElement('div');
  overlay.id = 'mwl-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${bgColor};
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999999;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 18px;
    color: ${textColor};
  `;
  overlay.innerHTML = '<div>Loading more...</div>';
  document.body.appendChild(overlay);
};

const hideLoadingOverlay = () => {
  const overlay = document.getElementById('mwl-loading-overlay');
  if (overlay) {
    overlay.remove();
    return true;
  }

  return false;
};

// Let's create a queue for the dynamic content
let mwlDynamicContentQueue = [];

// Updated function to add images to the queue
const addToMeowLightboxQueue = (imgElement) => {
  if (!(imgElement instanceof HTMLElement) || imgElement.tagName.toLowerCase() !== 'img') {
    mwl_log('🚫 Invalid image element:', imgElement);
    mwl_log('ℹ️ The parameter should be an img HTML element');
    return;
  }

  //If the lement is already in the queue, do not add it again
  for (const item of mwlDynamicContentQueue) {
    if (item.element === imgElement) {
      mwl_log('ℹ️ Image element is already in the Meow Lightbox queue:', imgElement.src);
      return;
    }
  }

  const existingId = imgElement.getAttribute('data-mwl-img-id');
  if (existingId) {
    // Image already has an ID, no need to add to queue
    mwl_log('ℹ️ Image already has data-mwl-img-id:', existingId);
    return;
  }

  
  const imageUrl = imgElement.getAttribute('data-src') || imgElement.getAttribute('src');
  if (!imageUrl) {
    mwl_log('🚫 Image element does not have a src attribute:', imgElement);
    return;
  }

  // Try to get the media ID from data-mwl-img-id or wp-image-[id] class
  let mediaId = null;
  // Extract ID from wp-image-[id] class
  const classAttr = imgElement.getAttribute('class');
  if (classAttr) {
    const match = classAttr.match(/wp-image-(\d+)/);
    if (match) {
      mediaId = match[1];
    }
  }
  

  mwlDynamicContentQueue.push({ element: imgElement, url: imageUrl, id: mediaId });
};

// Updated function using fetch
const regenerate_mwl_data = async (images) => {
  try {
    const response = await fetch(`${apiUrl}regenerate_mwl_data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        images,
        page_url: window.location.href // Send the current page URL
      }),
    });

    // Check if the response is OK
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
      return data.data;
    } else {
      mwl_log('Server responded with an error:', data);
      return null;
    }
  } catch (err) {
    if (err.message) {
      alert(err.message);
    }
    return null;
  }
};

const renderMeowLightboxWithSelector = async ( selector, delay = 0 ) => {
  const containers = document.querySelectorAll(selector);
  containers.forEach(container => {
    if (anti_selector && container.matches(anti_selector)) {
      mwl_log(`⚠️ Meow Lightbox: The container ${selector} matches the anti-selector ${anti_selector}. It will be ignored.`);
      return;
    }
    const images = container.querySelectorAll('img');
    images.forEach(img => {
      // Check if the image itself matches the anti-selector or is inside an anti-selector element
      if (anti_selector) {
        if (img.matches(anti_selector) || img.closest(anti_selector)) {
          return;
        }
      }
      addToMeowLightboxQueue(img);
    });
  });
  await renderMeowLightbox(delay);
};

const renderMeowLightboxWithParentElement = async ( parentElement, delay = 0 ) => {
  if (!(parentElement instanceof HTMLElement)) {
    mwl_log('🚫 Invalid parent element:', parentElement);
    return;
  }

  if (anti_selector && parentElement.matches(anti_selector)) {
    mwl_log(`⚠️ Meow Lightbox: The parent element matches the anti-selector ${anti_selector}. It will be ignored.`);
    return;
  }

  const images = parentElement.querySelectorAll('img');
  images.forEach(img => {
    // Check if the image itself matches the anti-selector or is inside an anti-selector element
    if (anti_selector) {
      if (img.matches(anti_selector) || img.closest(anti_selector)) {
        return;
      }
    }
    addToMeowLightboxQueue(img);
  });

  await renderMeowLightbox(delay);
};


const destroyFromMeowLightbox = async (parentElement) => {
  if (!(parentElement instanceof HTMLElement)) {
    mwl_log('🚫 Invalid parent element:', parentElement);
    return;
  }

  // Find all elements with data-mwl-img-id within the parent
  const elementsWithId = parentElement.querySelectorAll('[data-mwl-img-id]');
  
  if (elementsWithId.length === 0) {
    mwl_log('ℹ️ No elements with data-mwl-img-id found in parent');
    return;
  }

  const idsToRemove = [];
  
  elementsWithId.forEach(el => {
    const id = el.getAttribute('data-mwl-img-id');
    if (id) {
      idsToRemove.push(id);
      el.removeAttribute('data-mwl-img-id');
    }
  });

  // Remove the IDs from mwl_data
  if (window.mwl_data && idsToRemove.length > 0) {
    idsToRemove.forEach(id => {
      delete window.mwl_data[id];
    });
    mwl_log('🗑️ Removed', idsToRemove.length, 'entries from mwl_data:', idsToRemove);
  }

  // Re-render the lightbox
  await renderMeowLightbox();
};

const renderMeowLightbox = async ( delayLog = 0 ) => {
  mwl_log('🎞️ Rendering Meow Lightbox with delay:', delayLog);


  // If there is content in the queue, send a request to get the new mwl_data value
  if (mwlDynamicContentQueue.length > 0) {
    
    // Take a snapshot of the queue to process and clear the global queue
    const queueToProcess = [...mwlDynamicContentQueue];
    mwlDynamicContentQueue = [];

    mwl_log('🔍 Processing queue with', queueToProcess.length, 'items', queueToProcess);

    // Check for images already in mwl_data (by ID or URL via mwl_url_index)
    if (window.mwl_data) {
      queueToProcess.forEach(item => {
        // Try by ID first
        if (item.id && window.mwl_data[item.id]) {
          item.element.setAttribute('data-mwl-img-id', item.id);
          return;
        }
        // Try by URL using the PHP-generated index
        if (window.mwl_url_index && item.url) {
          const idFromUrl = window.mwl_url_index[item.url];
          if (idFromUrl && window.mwl_data[idFromUrl]) {
            item.element.setAttribute('data-mwl-img-id', idFromUrl);
            return;
          }
        }
      });
    }

    // Filter queue to only items that still need fetching
    const itemsToFetch = queueToProcess.filter(item => !item.element.getAttribute('data-mwl-img-id'));
    
    // If we have dynamic cache, all dynamic content should already be in mwl_data
    const hasDynamicCache = window.mwl_data_has_dynamic_cache === true;
    
    if (hasDynamicCache && itemsToFetch.length === 0) {
      mwl_log('✅ All dynamic content is cached in page. Skipping REST call.');
    } else if (skip_dynamic_fetch && itemsToFetch.length > 0) {
      // Skip REST call and treat images as orphans
      mwl_log('⏭️ Skipping dynamic fetch (skip_dynamic_fetch enabled). Images will be treated as orphans.');
      
      const mwlDataUpdates = {};
      const urlIndexUpdates = {};
      
      // Generate orphan entries for these images
      itemsToFetch.forEach((item, index) => {
        const imgSrc = item.element.getAttribute('mgl-src') || item.element.src || '';
        
        // Check if this URL already exists in mwl_url_index
        const existingId = window.mwl_url_index?.[imgSrc];
        if (existingId) {
          // Reuse existing ID instead of creating duplicate
          item.element.setAttribute('data-mwl-img-id', existingId);
          return;
        }
        
        const orphanId = `orphan-dynamic-${Date.now()}-${index}`;
        item.element.setAttribute('data-mwl-img-id', orphanId);
        
        // Add to index to prevent duplicates within this batch
        urlIndexUpdates[imgSrc] = orphanId;
        
        // Create basic orphan data entry using helper
        mwlDataUpdates[orphanId] = createOrphanMwlDataEntry(orphanId, item.element);
      });
      
      // Merge orphan data into mwl_data and update URL index
      window.mwl_data = { ...window.mwl_data, ...mwlDataUpdates };
      window.mwl_url_index = { ...window.mwl_url_index, ...urlIndexUpdates };
      mwl_log('✅ Added', Object.keys(mwlDataUpdates).length, 'orphan entries to mwl_data');
    } else if (itemsToFetch.length > 0) {
      // Deduplicate URLs and preserve IDs
      const uniqueImages = new Map();
      itemsToFetch.forEach(item => {
        if (!uniqueImages.has(item.url)) {
          uniqueImages.set(item.url, { url: item.url, id: item.id });
        }
      });
      const imagesToSend = Array.from(uniqueImages.values());

      mwl_log('🚚 Sending request to get the new mwl_data for', imagesToSend.length, 'images');
      const newImages = await regenerate_mwl_data(imagesToSend);

      if (newImages) {
        // Build mwl_data as { id: data }
        const mwlDataUpdates = {};
        const urlIndexUpdates = {};
        
        newImages.forEach((imageData) => {
          if ( imageData.data.success === false ) {
            mwl_log('🔴 Error fetching image data:', imageData.id, imageData.data);
            return;
          }

          mwlDataUpdates[imageData.id] = imageData.data;
          
          // Update URL index
          if (imageData.data.file) {
            urlIndexUpdates[imageData.data.file] = imageData.id;
          }

          // Find ALL corresponding items in the queue
          queueToProcess.forEach((item) => {
             if (item.url === imageData.url && item.element) {
                item.element.setAttribute('data-mwl-img-id', imageData.id);
             }
          });
        });

        // Update mwl_data and URL index
        window.mwl_data = { ...window.mwl_data, ...mwlDataUpdates };
        window.mwl_url_index = { ...window.mwl_url_index, ...urlIndexUpdates };
      }
    }
  }

  // Check if the lightbox is currently open (has .pswp--open class)
  const pswpElement = document.querySelector('.pswp--open');
  const isLightboxOpen = pswpElement !== null;
  
  if (isLightboxOpen) {
    // Get the current slide index to reopen at the same position
    const currentSlideIndex = window.mwl_pswp?.currIndex ?? 0;
    mwl_log('⏳ Lightbox is open at slide', currentSlideIndex, '- showing loading overlay');
    
    // Show loading overlay
    showLoadingOverlay();
    
    // Store the slide index to reopen (on window so core.js can access it)
    window.mwlReopenSlideIndex = currentSlideIndex;
    
    // Close the lightbox programmatically
    if (window.mwl_pswp?.close) {
      window.mwl_pswp.close();
    }
    
    // Wait for the lightbox to fully close, then rebuild
    setTimeout(() => {
      mwl_log('🔄 Rebuilding lightbox after close');
      
      // Remove any existing root element
      const existingRoot = document.getElementById('mwl-root');
      if (existingRoot) {
        render(null, existingRoot);
        existingRoot.parentNode.removeChild(existingRoot);
      }

      // Create a new root element
      document.body.appendChild(document.createRange().createContextualFragment('<div id="mwl-root"></div>'));

      // Render the new PhotoSwipe
      render(<PhotoSwipeCore />, document.getElementById('mwl-root'));
      
      // Fallback: hide loading overlay after 2 seconds if lightbox didn't open for some reason
      setTimeout(() => {
        const deleted = hideLoadingOverlay();
        if ( deleted ) {
          mwl_log('⚠️ Fallback: hiding loading overlay (lightbox may not have reopened)');
          window.mwlReopenSlideIndex = null;
        }
      }, 2000);
    }, 350); // Wait for close animation
    
    return;
  }

  // Remove any existing root element
  const existingRoot = document.getElementById('mwl-root');
  if (existingRoot) {
    render(null, existingRoot);
    existingRoot.parentNode.removeChild(existingRoot);
  }

  // Create a new root element
  document.body.appendChild(document.createRange().createContextualFragment('<div id="mwl-root"></div>'));

  // Only use PhotoSwipe
  render(<PhotoSwipeCore />, document.getElementById('mwl-root'));
};

/**
 * After a small timeout to ensure most things are loaded.
 */
document.addEventListener('DOMContentLoaded', () => {

  const delay = window.mwl_settings?.settings?.rendering_delay ?? 300;
  
  // Track if delayed render is complete (for deep linking to wait)
  window.mwlDelayedRenderComplete = (delay === 0);
  
  renderMeowLightbox();

  if( delay > 0 ) {
    setTimeout(() => {
      window.mwlDelayedRenderComplete = true;
      renderMeowLightboxWithSelector( selector, delay );
    }, delay);
  }
  
});

window.renderMeowLightboxWithSelector = renderMeowLightboxWithSelector;
window.renderMeowLightboxWithParentElement = renderMeowLightboxWithParentElement;
window.renderMeowLightbox = renderMeowLightbox;
window.addToMeowLightboxQueue = addToMeowLightboxQueue;
window.destroyFromMeowLightbox = destroyFromMeowLightbox;
